import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthService } from '../services/authService';
import { Logo } from '../components/icons/Logo';
import { EyeIcon, EyeOffIcon } from '../components/icons/Icons';
import toast from 'react-hot-toast';
import { Input, Button } from '../components/ui';
import { motion, useReducedMotion, type Variants } from 'motion/react';

const ResetPasswordPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const prefersReducedMotion = useReducedMotion();

    const token = searchParams.get('token');
    const emailParam = searchParams.get('email');

    // Page-load entrance variants: base=300ms, slow=500ms — numeric equivalents of
    // --snapfy-duration-base and --snapfy-duration-slow; easing mirrors --snapfy-ease-standard.
    const headingVariants: Variants = {
        hidden: prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: -12 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const } },
    };
    const cardVariants: Variants = {
        hidden: prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const, delay: 0.15 } },
    };

    useEffect(() => {
        if (emailParam) setEmail(emailParam);
        if (!token) {
            toast.error('Token inválido ou expirado.');
            navigate('/login');
        }
    }, [emailParam, token, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            toast.error('As senhas não coincidem.');
            return;
        }

        if (!token) return;

        setIsLoading(true);
        try {
            await AuthService.resetPassword({
                token,
                email,
                password,
                password_confirmation: confirmPassword
            });
            toast.success('Senha redefinida com sucesso!');
            navigate('/login');
        } catch (error: any) {
            console.error(error);
            toast.error(error.message || 'Falha ao redefinir senha.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-snapfy-bg flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <motion.div
                className="sm:mx-auto sm:w-full sm:max-w-md"
                variants={headingVariants}
                initial="hidden"
                animate="visible"
            >
                <div className="flex justify-center">
                    <Logo className="h-12 w-auto" />
                </div>
                <h2 className="mt-6 text-center text-3xl font-extrabold text-snapfy-text-primary">
                    Redefinir Senha
                </h2>
            </motion.div>

            <motion.div
                className="mt-8 sm:mx-auto sm:w-full sm:max-w-md"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
            >
                <div className="card bg-snapfy-surface py-8 px-4 shadow-card rounded-card sm:px-10 border border-snapfy-border">
                    <form className="space-y-6" onSubmit={handleSubmit}>
                        {/*
                          * email: Input does NOT forward refs. The emailParam read-only case relies
                          * only on the `readOnly` and `disabled` props, not a ref — safe to use Input.
                          */}
                        <Input
                            id="email"
                            name="email"
                            type="email"
                            label="Endereço de Email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            readOnly={!!emailParam}
                            disabled={!!emailParam}
                        />

                        <Input
                            id="password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            label="Nova Senha"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            trailingAffordance={
                                <button
                                    type="button"
                                    className="flex items-center text-snapfy-text-secondary hover:text-snapfy-text-primary"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                >
                                    {showPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                </button>
                            }
                        />

                        {/* confirmPassword: no toggle — confirm fields conventionally stay masked */}
                        <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            type="password"
                            label="Confirmar Nova Senha"
                            required
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />

                        <div>
                            <Button
                                type="submit"
                                variant="solid"
                                className="w-full"
                                isLoading={isLoading}
                                disabled={isLoading}
                            >
                                Redefinir Senha
                            </Button>
                        </div>
                    </form>
                </div>
            </motion.div>
        </div>
    );
};

export default ResetPasswordPage;
